// src/components/common/PaginationControls.tsx

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationControlsProps {
  currentPage: number;
  isLoading: boolean;
  onPrevPage: () => void;
  onNextPage: () => void;
  canGoPrev: boolean;
  canGoNext: boolean;
  // ============================================
  // <<<--- Nuevas props para rango X-Y --->>>
  itemsPerPage: number;
  countOnPage: number;
  // ============================================
  totalItems?: number; // Optional total count
  isMobile?: boolean; // Optional flag for mobile view
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  isLoading,
  onPrevPage,
  onNextPage,
  canGoPrev,
  canGoNext,
  // ==============================
  // <<<--- Recibir props --->>>
  itemsPerPage,
  countOnPage,
  // ==============================
  totalItems,
  isMobile = false
}) => {

  // ============================================
  // <<<--- Calcular rango X-Y --->>>
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = startItem + countOnPage - 1;
  // ============================================


  return (
    <div className="mt-4 px-4 py-3 flex items-center justify-between border-t border-line sm:px-6">
      {/* --- Vista Móvil (sin cambios) --- */}
      <div className={`flex-1 flex justify-between ${!isMobile ? 'sm:hidden' : ''}`}>
        <button onClick={onPrevPage} disabled={isLoading || !canGoPrev} className="relative inline-flex items-center px-4 py-2 border border-line text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed">
          Anterior
        </button>
        <button onClick={onNextPage} disabled={isLoading || !canGoNext} className="ml-3 relative inline-flex items-center px-4 py-2 border border-line text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed">
          Siguiente
        </button>
      </div>

      {/* --- Vista Escritorio (Modificada) --- */}
      {!isMobile && (
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"> {/* Volver a justify-between */}

          {/* <<<--- Texto "Mostrando X-Y" a la izquierda ---<<< */}
          <div>
            {countOnPage > 0 ? ( // Mostrar solo si hay items en la página actual
              <p className="text-sm text-content-2">
                Mostrando <span className="font-medium">{startItem}</span> a <span className="font-medium">{endItem}</span>
                {totalItems !== undefined && (
                  <> de <span className="font-medium">{totalItems}</span> total</>
                )}
              </p>
             ) : (
               <p className="text-sm text-content-2">No hay resultados en esta página.</p> // Mensaje alternativo
             )}
          </div>

          {/* <<<--- Botones y Página Actual a la derecha ---<<< */}
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              {/* Botón Anterior */}
              <button onClick={onPrevPage} disabled={isLoading || !canGoPrev} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-line bg-surface text-sm font-medium text-content-3 hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Anterior">
                <span className="sr-only">Anterior</span>
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>

              {/* Indicador de Página Actual */}
              <span aria-current="page" className="relative inline-flex items-center px-4 py-2 border border-line bg-accent/10 text-sm font-medium text-accent z-10">
                Página {currentPage}
              </span>

              {/* Botón Siguiente */}
              <button onClick={onNextPage} disabled={isLoading || !canGoNext} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-line bg-surface text-sm font-medium text-content-3 hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Siguiente">
                <span className="sr-only">Siguiente</span>
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaginationControls;